// Tiny markdown-ish renderer for blog article bodies.
//
// Supports:
//   • Raw HTML pass-through (existing seeded articles ship as HTML)
//   • # / ## / ### headings → <h3>/<h4>/<h4>
//   • **bold** → <strong>
//   • *italic* or _italic_ → <em>
//   • `code` → <code>
//   • Blank-line separated paragraphs → <p>
//   • Custom blocks:
//       ::: highlight Optional Label
//       body text…
//       :::
//       → <div class="highlight-box"><div class="hl-label">Label</div><p>body</p></div>
//
//       ::: numbers
//       value | label
//       value | label
//       :::
//       → <div class="numbers-row"><div class="num-box"><div class="nb-val">value</div><div class="nb-label">label</div></div>…</div>
//
// Not a general-purpose markdown parser — good enough for TrustLab's blog
// content model.

const escapeHtml = (s) => String(s)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

// Wrap raw HTML detection — if the block already looks like HTML (starts with
// a tag), we emit it as-is without paragraph-wrapping.
const looksLikeHtml = (s) => /^\s*</.test(s);

const applyInline = (s) => {
  return s
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/(?<!\*)\*(?!\s)([^*]+?)(?<!\s)\*(?!\*)/g, '<em>$1</em>')
    .replace(/_([^_]+)_/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code>$1</code>');
};

const renderParagraph = (text) => {
  const t = text.trim();
  if (!t) return '';
  if (looksLikeHtml(t)) return t;                       // raw HTML pass-through
  if (/^###\s+/.test(t)) return `<h4>${applyInline(t.replace(/^###\s+/, ''))}</h4>`;
  if (/^##\s+/.test(t))  return `<h4>${applyInline(t.replace(/^##\s+/, ''))}</h4>`;
  if (/^#\s+/.test(t))   return `<h3>${applyInline(t.replace(/^#\s+/, ''))}</h3>`;
  return `<p>${applyInline(t)}</p>`;
};

const renderHighlightBlock = (label, body) => {
  const labelHtml = label ? `<div class="hl-label">${escapeHtml(label)}</div>` : '';
  const bodyHtml = body.trim().split(/\n{2,}/).map(renderParagraph).join('');
  return `<div class="highlight-box">${labelHtml}${bodyHtml}</div>`;
};

const renderNumbersBlock = (body) => {
  const rows = body.trim().split(/\n+/).map((line) => {
    const [val, lbl] = line.split('|').map((x) => x?.trim() || '');
    return `<div class="num-box"><div class="nb-val">${escapeHtml(val)}</div><div class="nb-label">${escapeHtml(lbl)}</div></div>`;
  });
  return `<div class="numbers-row">${rows.join('')}</div>`;
};

export const renderBlogMarkdown = (src) => {
  if (!src) return '';
  const input = String(src).replace(/\r\n/g, '\n');

  // First extract :::block…::: regions so paragraph splitting doesn't touch them.
  const blocks = [];
  const SENTINEL = '@@BLOG_BLOCK_';
  let stripped = input.replace(
    /:::\s*(highlight|numbers)([^\n]*)\n([\s\S]*?)\n:::/g,
    (_m, kind, afterKind, body) => {
      const label = afterKind.trim();
      const html = kind === 'highlight'
        ? renderHighlightBlock(label, body)
        : renderNumbersBlock(body);
      blocks.push(html);
      return `\n\n${SENTINEL}${blocks.length - 1}@@\n\n`;
    },
  );

  const parts = stripped.split(/\n{2,}/).map((chunk) => {
    const placeholderMatch = chunk.match(new RegExp(`^${SENTINEL}(\\d+)@@$`));
    if (placeholderMatch) return blocks[Number(placeholderMatch[1])];
    return renderParagraph(chunk);
  });

  return parts.filter(Boolean).join('');
};

export default renderBlogMarkdown;
